// The mock-up's list panel: rows load 50 at a time as you scroll (no pager),
// under a sticky column header, with a "Showing x of y" footer.
//
// Drop-in for the antd <Table> on a master screen - it takes the very same
// `columns` array (title / dataIndex / render / sorter) and `dataSource`, so a
// page only swaps the element. Rendering only: it never fetches, filters or
// mutates a row; sorting uses the column's own `sorter`.
import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_BATCH = 50;

// "a.b.c" or ["a","b"] or "name"
const cellValue = (record, dataIndex) => {
  if (record == null || dataIndex == null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : String(dataIndex).split(".");
  return path.reduce((acc, k) => (acc == null ? acc : acc[k]), record);
};

const isActions = (col) =>
  col?.dataIndex === "actions" || col?.key === "actions" || col?.title === "Actions";

const MasterList = ({
  columns = [],
  dataSource = [],
  batch = DEFAULT_BATCH,
  rowKey,
  selectedKey,
  onRowClick,
  emptyTitle = "Nothing matches",
  emptyHint = "Try a shorter search, or reset the filters.",
}) => {
  const cols = useMemo(() => (columns || []).filter(Boolean), [columns]);
  const [sort, setSort] = useState({ index: -1, dir: 1 });
  const [shown, setShown] = useState(batch);
  const wrapRef = useRef(null);
  const sentinelRef = useRef(null);

  const rows = useMemo(() => {
    const list = Array.isArray(dataSource) ? dataSource.slice() : [];
    const col = cols[sort.index];
    if (col && typeof col.sorter === "function") {
      list.sort((a, b) => {
        const r = col.sorter(a, b);
        return (Number.isFinite(r) ? r : 0) * sort.dir;
      });
    }
    return list;
  }, [dataSource, cols, sort]);

  // A new result set (search, filter, refresh, sort) starts from the first
  // batch again. Keyed on what the rows are rather than on the array's
  // identity: the parent rebuilds its filtered array on every render, and
  // re-reading it must not throw away batches the user already scrolled past.
  const signature = useMemo(() => {
    const list = Array.isArray(dataSource) ? dataSource : [];
    const edge = (r) => (r == null ? "" : r.guid ?? r.id ?? "");
    return list.length + "|" + edge(list[0]) + "|" + edge(list[list.length - 1]);
  }, [dataSource]);

  useEffect(() => {
    setShown(batch);
    if (wrapRef.current) wrapRef.current.scrollTop = 0;
  }, [signature, sort, batch]);

  // load the next batch as the bottom of the list comes into view
  useEffect(() => {
    const root = wrapRef.current;
    const target = sentinelRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return;
    if (shown >= rows.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setShown((n) => n + batch);
      },
      { root, rootMargin: "240px" }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [shown, rows.length, batch]);

  const keyOf = (record, i) => {
    if (typeof rowKey === "function") return rowKey(record, i);
    if (rowKey) return cellValue(record, rowKey);
    return record?.id ?? record?.guid ?? i;
  };

  const toggleSort = (index) =>
    setSort((s) => (s.index === index ? { index, dir: -s.dir } : { index, dir: 1 }));

  const visible = rows.slice(0, shown);

  return (
    <div className="ax-list">
      <div className="ax-listwrap" ref={wrapRef}>
        <table className="ax-listtable">
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th
                  key={c.key || c.dataIndex || i}
                  className={isActions(c) ? "r" : ""}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {typeof c.sorter === "function" ? (
                    <button type="button" className="ax-sortbtn" onClick={() => toggleSort(i)}>
                      {c.title}
                      <span className="ax-sortmark">
                        {sort.index === i ? (sort.dir > 0 ? "↑" : "↓") : ""}
                      </span>
                    </button>
                  ) : (
                    c.title
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((record, i) => {
              const key = keyOf(record, i);
              const selected = selectedKey != null && key === selectedKey;
              return (
                <tr
                  key={key}
                  className={
                    (selected ? "ax-rowsel" : "") + (onRowClick ? " ax-rowclick" : "")
                  }
                  onClick={onRowClick ? () => onRowClick(record) : undefined}
                >
                  {cols.map((c, ci) => (
                    <td
                      key={c.key || c.dataIndex || ci}
                      className={isActions(c) ? "r" : ""}
                      // the edit / delete icons keep their own click; the row's
                      // "open this record" must not fire underneath them
                      onClick={isActions(c) && onRowClick ? (e) => e.stopPropagation() : undefined}
                    >
                      {typeof c.render === "function"
                        ? c.render(cellValue(record, c.dataIndex), record, i)
                        : cellValue(record, c.dataIndex)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="ax-sentinel" ref={sentinelRef} />

        {shown < rows.length && (
          <div className="ax-loadmore">
            <button type="button" onClick={() => setShown((n) => n + batch)}>
              Load {batch} more
            </button>
          </div>
        )}

        {!rows.length && (
          <div className="ax-noresult">
            <div className="big">{emptyTitle}</div>
            <div>{emptyHint}</div>
          </div>
        )}
      </div>

      <div className="ax-listfoot">
        <span>
          Showing <b>{visible.length}</b> of <b>{rows.length}</b>
        </span>
        <span className="right">
          {shown >= rows.length ? "all loaded" : `${batch} per batch · scroll for more`}
        </span>
      </div>
    </div>
  );
};

export default MasterList;
